import { create } from 'zustand';
import { dev } from '@breath-lab/domain';
import type { BLUser, UserSettings } from '@breath-lab/types';
import { getServices, type AuthUser } from '@breath-lab/services';

interface AuthState {
  user: BLUser | null;
  firebaseUser: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isLoginModalOpen: boolean;

  openLoginModal: (redirectTo?: string) => void;
  closeLoginModal: () => void;
  signInWithGoogle: () => Promise<void>;
  /** iOS-native only. Throws on web + Android; sign-in screens must feature-detect. */
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string) => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  verifyPasswordResetCode: (code: string) => Promise<string>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: (provider: 'google' | 'apple' | 'password', email?: string, password?: string) => Promise<void>;
  updateUserSettings: (settings: UserSettings) => Promise<void>;
  updateUserDisplayName: (displayName: string) => Promise<void>;
  recordOnboardedAt: () => Promise<void>;
}

let _unsubUserListener: (() => void) | null = null;
let _authListenerInitialized = false;
let _revenueCatEnabled = false;
// Monotonic auth-event generation. Bumped on every onAuthStateChanged
// invocation so a suspended callback can detect it has been superseded by a
// newer sign-in/sign-out and abort its stale continuation.
let _authGen = 0;

/** Test-only — undo the one-time guard so tests can re-init the listener. */
export function _resetAuthListenerForTesting(): void {
  _unsubUserListener?.();
  _unsubUserListener = null;
  _authListenerInitialized = false;
  _revenueCatEnabled = false;
  _rcBoundUid = null;
  _authGen = 0;
  // Clear the single late-bind slot too, so a spy registered by one test can
  // never fire in the next. useSubscription re-registers it at module load.
  onRcBound = null;
}

/** Test-only — toggle RC binding without wiring the full auth listener. */
export function _setRcBindingEnabledForTesting(enabled: boolean): void {
  _revenueCatEnabled = enabled;
}

// How long the auth listener waits for the RC bind before letting the UI
// proceed. Generous on purpose (user preference): give RC a real chance to
// settle in the foreground so subscription state is correct on first render;
// the race only rescues pathological hangs.
const RC_BIND_TIMEOUT_MS = 10_000;

// Set on every bindRevenueCat() success. useSubscription gates its store
// hydration on this so customer info fetched under a previous or anonymous
// RC identity can never be attributed to the current Firebase user (web RC
// has no logOut — the SDK stays bound to the last identity).
let _rcBoundUid: string | null = null;

/** The Firebase uid the RC SDK is currently confirmed bound to, or null. */
export function getRcBoundUid(): string | null {
  return _rcBoundUid;
}

/** Whether RC identity binding is switched on for this build. */
export function isRcBindingEnabled(): boolean {
  return _revenueCatEnabled;
}

/** Test-only — seed/clear the RC bound-uid marker. */
export function _setRcBoundUidForTesting(uid: string | null): void {
  _rcBoundUid = uid;
}

/*
 * RevenueCat identity lifecycle (degraded policy):
 *
 *   app bootstrap ── purchases.configure(apiKey)        (anonymous; per app)
 *        │
 *   Firebase auth resolves uid
 *        │
 *   bindRevenueCat(uid) ── purchases.logIn(uid) ──ok──▶ _rcBoundUid = uid
 *        │                                              setAttributes(...)
 *        └──fail──▶ dev.warn, login CONTINUES           prefetchSubscription
 *                   (premium falls back to Firestore
 *                    accessLevel via computeIsPremium)
 *        │
 *   purchase / restore ── useSubscription re-runs logIn(uid) and ABORTS on
 *   failure — a transaction can never be attributed to an anonymous RC user.
 */

/**
 * Best-effort bind of the RC App User ID to this Firebase uid. Failure is
 * logged and swallowed: login must never depend on RevenueCat availability
 * (offline launch, RC outage). Correctness is enforced elsewhere — premium
 * display falls back to the Firestore accessLevel, and useSubscription's
 * purchase/restore gate re-runs logIn and refuses to transact unbound.
 *
 * Safe to call multiple times for the same uid; RC treats a repeat logIn as
 * a no-op once the identity is bound.
 *
 * No-op when RC is not enabled for this build (tests, dev without an API
 * key).
 *
 * @returns true when the bind succeeded (or RC is disabled), false on failure.
 */
export async function bindRevenueCat(uid: string): Promise<boolean> {
  if (!_revenueCatEnabled) return true;
  try {
    await getServices().purchases.logIn(uid);
    _rcBoundUid = uid;
    return true;
  } catch (err) {
    dev.warn('[rc] logIn failed — continuing without RC binding', err);
    return false;
  }
}

// Post-bind side effects: RC attributes (email, displayName, fcm token).
// Only runs after a CONFIRMED bind — attaching attributes to an anonymous
// RC user would decorate an identity we refuse to transact with.
function applyRcAttributes(firebaseUser: AuthUser, blUser: BLUser | null): void {
  const purchases = getServices().purchases;
  const attrs: Partial<Record<'email' | 'displayName' | 'fcmToken', string | null>> = {};
  if (firebaseUser.email) attrs.email = firebaseUser.email;
  const displayName = blUser?.displayName ?? firebaseUser.displayName;
  if (displayName) attrs.displayName = displayName;
  if (Object.keys(attrs).length > 0) {
    dev.log('[rc] setting attributes:', Object.keys(attrs));
    purchases.setAttributes(attrs).catch(() => {});
  }

  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    getServices().messaging.requestPermissionAndGetToken().then((token) => {
      if (token) {
        dev.log('[rc] setting fcm token');
        purchases.setAttributes({ fcmToken: token }).catch(() => {});
      }
    });
  }
}

export interface InitAuthListenerOptions {
  /**
   * Enable RC identity binding after auth resolves. Each app bootstrap is
   * responsible for calling purchases.configure(apiKey) itself BEFORE
   * initAuthListener; this flag only switches the logIn(uid) binding on.
   */
  revenueCatEnabled?: boolean;
  /** Side-effects to run after the user is hydrated (e.g. preload public/user data). */
  onSignedIn?: (uid: string) => void;
  /** Side-effects to run after sign-out (e.g. reset user-scoped stores). */
  onSignedOut?: () => void;
}

/**
 * Wire the auth state listener that hydrates the Zustand store. Called once
 * from the app's bootstrap AFTER `setServices(...)`. Splitting it out of the
 * Zustand `create()` factory avoids racing the service container.
 */
export function initAuthListener(options: InitAuthListenerOptions = {}): void {
  if (_authListenerInitialized) return;
  _authListenerInitialized = true;
  const { revenueCatEnabled, onSignedIn, onSignedOut } = options;
  _revenueCatEnabled = !!revenueCatEnabled;
  const set = (partial: Partial<AuthState>) => useAuth.setState(partial);

  // Eagerly consume any pending Google redirect-result so Firebase Auth wakes
  // up and fires onAuthStateChanged with the new user. Per Firebase docs,
  // signInWithRedirect requires getRedirectResult to be called on page load.
  const pendingRedirectUser = getServices().userMapper.handleGoogleRedirectResult().catch(() => null);

  getServices().auth.onAuthStateChanged(async (firebaseUser) => {
    // onAuthStateChanged is async and NOT serialized: a newer auth event (B
    // signs in, or sign-out) can fire while this callback is still suspended
    // at an await. Capture the generation on entry and bail from any stale
    // continuation — otherwise A's late continuation overwrites B's state,
    // resurrects A's user-doc listener, re-runs onSignedIn(A), or attaches
    // A's PII to the RC identity B now owns.
    const gen = ++_authGen;
    const isStale = (): boolean => gen !== _authGen;

    if (firebaseUser) {
      dev.log('[auth] signed in uid:', firebaseUser.uid);
      const userMapper = getServices().userMapper;
      const redirectUser = await pendingRedirectUser;
      if (isStale()) return;
      const blUser = redirectUser ?? await userMapper.fetchUser();
      if (isStale()) return;

      dev.log('[rc] enabled:', _revenueCatEnabled);
      if (_revenueCatEnabled) {
        // Wait up to RC_BIND_TIMEOUT_MS for the bind so subscription state is
        // usually settled before the first render, but never block login
        // beyond that — offline/RC-outage users still get in (degraded:
        // premium falls back to Firestore accessLevel). If the bind lands
        // AFTER the timeout, the late branch below hydrates the store.
        // raceSettled flips AFTER the race resolves; a bind completing later
        // is by definition "late" and must re-hydrate the subscription store
        // (the sign-in prefetch saw RC unbound and was skipped by the
        // boundUid guard). A bind completing before the race settles takes
        // the normal path — the useSubscription mount effect prefetches once
        // a consumer renders, now that the boundUid guard passes.
        let raceSettled = false;
        const bindPromise = bindRevenueCat(firebaseUser.uid).then((bound) => {
          // Re-check the generation: a bind landing after a newer auth event
          // must not attach this (now-previous) user's attributes or hydrate.
          if (bound && _revenueCatEnabled && !isStale()) {
            applyRcAttributes(firebaseUser, blUser);
            if (raceSettled) onRcBound?.(firebaseUser.uid);
          }
        });
        await Promise.race([
          bindPromise,
          new Promise<void>((resolve) => setTimeout(resolve, RC_BIND_TIMEOUT_MS)),
        ]);
        if (isStale()) return;
        raceSettled = true;
      }

      set({ firebaseUser, user: blUser, isLoading: false, isAuthenticated: true });

      _unsubUserListener?.();
      _unsubUserListener = userMapper.listenToUser(firebaseUser.uid, (user) => {
        set({ user });
      });

      onSignedIn?.(firebaseUser.uid);
    } else {
      _rcBoundUid = null;
      _unsubUserListener?.();
      _unsubUserListener = null;
      onSignedOut?.();
      set({ firebaseUser: null, user: null, isLoading: false, isAuthenticated: false });
    }
  });
}

// Wired by useSubscription (avoids a circular import): re-hydrates the
// subscription store when an RC bind completes after the listener's timeout.
let onRcBound: ((uid: string) => void) | null = null;
export function _registerOnRcBound(cb: (uid: string) => void): void {
  onRcBound = cb;
}

export const useAuth = create<AuthState>((set, get) => {
  return {
    user: null,
    firebaseUser: null,
    isLoading: true,
    isAuthenticated: false,
    isLoginModalOpen: false,

    openLoginModal: (redirectTo) => {
      const sessionStorage = getServices().sessionStorage;
      if (redirectTo) {
        sessionStorage.set('authRedirectFrom', redirectTo);
      } else {
        sessionStorage.remove('authRedirectFrom');
      }
      set({ isLoginModalOpen: true });
    },
    closeLoginModal: () => set({ isLoginModalOpen: false }),

    signInWithGoogle: async () => {
      const blUser = await getServices().userMapper.signInWithGoogle();
      if (blUser) {
        // Popup succeeded — best-effort RC bind; failure never blocks login.
        await bindRevenueCat(blUser.uid);
        const firebaseUser = getServices().auth.currentUser;
        set({ user: blUser, firebaseUser, isAuthenticated: true, isLoginModalOpen: false });
      }
      // null = redirect fallback initiated, page will navigate away. RC
      // binding happens in the auth listener once the redirect resolves.
    },

    signInWithApple: async () => {
      const apple = getServices().userMapper.signInWithApple;
      if (!apple) throw new Error('Apple Sign In is not available on this platform');
      const blUser = await apple.call(getServices().userMapper);
      if (blUser) {
        await bindRevenueCat(blUser.uid);
        const firebaseUser = getServices().auth.currentUser;
        set({ user: blUser, firebaseUser, isAuthenticated: true, isLoginModalOpen: false });
      }
    },

    signInWithEmail: async (email, password) => {
      const blUser = await getServices().userMapper.signInWithEmail(email, password);
      await bindRevenueCat(blUser.uid);
      const firebaseUser = getServices().auth.currentUser;
      set({ user: blUser, firebaseUser, isAuthenticated: true, isLoginModalOpen: false });
    },

    registerWithEmail: async (email, password) => {
      const blUser = await getServices().userMapper.registerWithEmail(email, password);
      await bindRevenueCat(blUser.uid);
      const firebaseUser = getServices().auth.currentUser;
      set({ user: blUser, firebaseUser, isAuthenticated: true });
    },

    sendPasswordResetEmail: async (email) => {
      await getServices().auth.sendPasswordResetEmail(email);
    },

    verifyPasswordResetCode: async (code) => {
      return getServices().auth.verifyPasswordResetCode(code);
    },

    confirmPasswordReset: async (code, newPassword) => {
      await getServices().auth.confirmPasswordReset(code, newPassword);
    },

    signOut: async () => {
      dev.log('[auth] signed out uid:', get().firebaseUser?.uid);
      await getServices().userMapper.signOut();
      // Clear the RC App User ID so the next signed-in user doesn't inherit
      // this user's entitlements via cached Keychain state.
      _rcBoundUid = null;
      await getServices().purchases.logOut().catch((err) => {
        dev.warn('[rc] logOut failed', err);
      });
      set({ user: null, firebaseUser: null, isAuthenticated: false });
    },

    deleteAccount: async (provider, email?, password?) => {
      const uid = get().firebaseUser?.uid;
      dev.log('[auth] delete account requested uid:', uid);
      if (provider === 'google') {
        await getServices().userMapper.deleteAccount({ provider: 'google' });
      } else if (provider === 'apple') {
        await getServices().userMapper.deleteAccount({ provider: 'apple' });
      } else {
        await getServices().userMapper.deleteAccount({ provider: 'password', email: email!, password: password! });
      }
      // Clear the RC App User ID after account deletion so the device doesn't
      // hold a Keychain link to a now-deleted Firebase uid.
      _rcBoundUid = null;
      await getServices().purchases.logOut().catch((err) => {
        dev.warn('[rc] logOut failed', err);
      });
      dev.log('%c[auth] account deleted uid: ' + uid, 'color: #ef4444; font-weight: bold');
      set({ user: null, firebaseUser: null, isAuthenticated: false });
    },

    updateUserSettings: async (settings) => {
      const { user } = get();
      if (!user) return;
      const previous = user;
      const updated: BLUser = { ...user, settings };
      set({ user: updated });
      try {
        await getServices().userMapper.updateUser(updated);
      } catch (e) {
        set({ user: previous });
        throw e;
      }
    },

    updateUserDisplayName: async (displayName) => {
      const { user } = get();
      if (!user) return;
      const previous = user;
      const updated: BLUser = { ...user, displayName };
      set({ user: updated });
      try {
        await getServices().userMapper.updateUser(updated);
      } catch (e) {
        set({ user: previous });
        throw e;
      }
    },

    recordOnboardedAt: async () => {
      const { user, firebaseUser } = get();
      if (!firebaseUser || user?.onboardedAt) return;
      const now = new Date();
      set({ user: user ? { ...user, onboardedAt: now } : user });
      await getServices().userMapper.recordOnboardedAt(firebaseUser.uid);
    },
  };
});
